'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Offer {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  discount: string;
  validUntil: string;
  ctaText: string;
  ctaLink: string;
}

interface SpecialOffersCarouselProps {
  offers: Offer[];
}

const SpecialOffersCarousel = ({ offers }: SpecialOffersCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % offers.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, offers.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + offers.length) % offers.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % offers.length);
  };

  if (!isHydrated) {
    return (
      <section className="py-12 px-6 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="h-96 bg-muted rounded-lg animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-foreground mb-2">
            Special Offers & Deals
          </h2>
          <p className="text-muted-foreground">
            Save big on your favorite seafood
          </p>
        </div>

        <div className="relative">
          <div className="relative h-96 rounded-lg overflow-hidden shadow-elevation-md">
            <AppImage
              src={offers[currentIndex].image}
              alt={offers[currentIndex].alt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
            
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-2xl px-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-full font-bold text-sm mb-4">
                  <Icon name="TagIcon" size={16} />
                  {offers[currentIndex].discount} OFF
                </div>
                <h3 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
                  {offers[currentIndex].title}
                </h3>
                <p className="text-white/90 text-lg mb-6 max-w-xl">
                  {offers[currentIndex].description}
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2 text-white/80">
                    <Icon name="ClockIcon" size={18} />
                    <span className="text-sm">Valid until {offers[currentIndex].validUntil}</span>
                  </div>
                </div>
                <Link
                  href={offers[currentIndex].ctaLink}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-md font-bold text-lg transition-smooth hover:shadow-elevation-lg hover-lift"
                >
                  <span>{offers[currentIndex].ctaText}</span>
                  <Icon name="ArrowRightIcon" size={20} />
                </Link>
              </div>
            </div>

            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-full transition-smooth"
              aria-label="Previous offer"
            >
              <Icon name="ChevronLeftIcon" size={24} />
            </button>

            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-full transition-smooth"
              aria-label="Next offer"
            >
              <Icon name="ChevronRightIcon" size={24} />
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {offers.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-smooth ${
                    index === currentIndex ? 'bg-white w-8' : 'bg-white/50'
                  }`}
                  aria-label={`Go to offer ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecialOffersCarousel;